import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { Button } from "@babylonjs/gui/2D/controls/button";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { ScrollViewer } from "@babylonjs/gui/2D/controls/scrollViewers/scrollViewer";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { GAMES } from "../content/registry";
import { t, getLang, setLang, type Lang } from "../genesis/i18n";
import type { CapacityManager } from "../genesis/CapacityQueue";
import { genSessionCode } from "../genesis/MultiplayerSystem";
import type { GameDef } from "../genesis/schema";

const OCEAN = "#0e2233";
const GOLD = "#d8b24a";

/** La Cité — the hub listing the 15 games ("15 clés"), with keys + live capacity. */
export class CiteHub {
  private adt: AdvancedDynamicTexture | null = null;

  constructor(private readonly capacity: CapacityManager) {}

  show(onSelect: (slug: string) => void): void {
    this.hide();
    const adt = AdvancedDynamicTexture.CreateFullscreenUI("kq-hub");
    adt.idealWidth = 1280;
    adt.useSmallestIdeal = true;
    this.adt = adt;

    const bg = new Rectangle("hubBg");
    bg.width = 1;
    bg.height = 1;
    bg.thickness = 0;
    bg.background = "rgba(4,12,20,0.55)";
    adt.addControl(bg);

    const panel = new Rectangle("hubPanel");
    panel.width = "720px";
    panel.height = "0.92";
    panel.cornerRadius = 20;
    panel.thickness = 2;
    panel.color = GOLD;
    panel.background = "rgba(10,26,38,0.96)";
    adt.addControl(panel);

    const header = new StackPanel();
    header.height = "112px";
    header.paddingTop = "18px";
    header.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    panel.addControl(header);

    header.addControl(text("🗝️ " + t({ fr: "LES CLÉS DU ROYAUME", en: "KEYS OF THE KINGDOM" }), 26, GOLD, "38px"));
    header.addControl(
      text(
        t({ fr: "« Je te donnerai les clés du royaume des cieux » — Mt 16:19", en: "\"I will give you the keys of the kingdom\" — Mt 16:19" }),
        14,
        "#9fc3d8",
        "24px",
      ),
    );

    const keys = loadKeys();
    header.addControl(
      text(
        t({ fr: `${keys.size}/15 clés obtenues`, en: `${keys.size}/15 keys earned` }),
        15,
        "#a9d6c0",
        "24px",
      ),
    );

    // Language toggle
    const langRow = new StackPanel();
    langRow.isVertical = false;
    langRow.height = "30px";
    langRow.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    langRow.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    langRow.paddingTop = "14px";
    langRow.paddingRight = "16px";
    panel.addControl(langRow);
    (["fr", "en"] as Lang[]).forEach((l) => {
      const b = Button.CreateSimpleButton("lang" + l, l.toUpperCase());
      b.width = "42px";
      b.height = "26px";
      b.fontSize = 13;
      b.cornerRadius = 6;
      b.color = "#f2ede0";
      b.background = getLang() === l ? "#2c6b3f" : "#163247";
      b.paddingRight = "4px";
      b.onPointerUpObservable.add(() => {
        setLang(l);
        this.show(onSelect); // rebuild in new language
      });
      langRow.addControl(b);
    });

    // Scrollable list of the 15 games
    const scroll = new ScrollViewer("hubScroll");
    scroll.width = 1;
    scroll.height = "0.72";
    scroll.top = "118px";
    scroll.thickness = 0;
    scroll.barColor = GOLD;
    scroll.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    panel.addControl(scroll);

    const list = new StackPanel();
    list.paddingLeft = "18px";
    list.paddingRight = "18px";
    scroll.addControl(list);

    GAMES.forEach((g, i) => {
      const done = keys.has(g.slug);
      const cap = this.capacity.status(g.slug);

      // v9 — one line per game: [play row][👥 group-play button]
      const line = new StackPanel("line" + g.slug);
      line.isVertical = false;
      line.height = "58px";
      list.addControl(line);

      const row = Button.CreateSimpleButton("g" + g.slug, "");
      row.width = "545px";
      row.height = "58px";
      row.paddingBottom = "8px";
      row.thickness = 1;
      row.color = done ? GOLD : "#2b4a5e";
      row.background = done ? "rgba(44,107,63,0.35)" : "#14283a";
      row.cornerRadius = 12;

      const label = new TextBlock();
      label.text = `${done ? "🗝️" : "▶"}  ${i + 1}. ${t(g.title)}`;
      label.color = "#f2ede0";
      label.fontSize = 15;
      label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      label.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
      label.paddingLeft = "18px";
      label.paddingTop = "7px";
      row.addControl(label);

      // v9 — supported-controls badges (data-driven, under the title before
      // the capacity chip): ⌨️ keyboard everywhere, 🎮 only when the game's
      // DATA proves stick-driven movement is central, 👆 touch always.
      const badges = new TextBlock();
      badges.text = stickFriendly(g) ? "⌨️🎮👆" : "⌨️👆";
      badges.fontSize = 12;
      badges.color = "#9fc3d8";
      badges.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      badges.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      badges.paddingLeft = "20px";
      badges.paddingBottom = "6px";
      row.addControl(badges);

      const capLbl = new TextBlock();
      const full = cap.load >= cap.capacity;
      capLbl.text = full
        ? t({ fr: `salle pleine · ${cap.queued} en file`, en: `full · ${cap.queued} in line` })
        : t({ fr: `${cap.load}/${cap.capacity} salles`, en: `${cap.load}/${cap.capacity} rooms` });
      capLbl.color = full ? "#e0a35a" : "#7fa6bd";
      capLbl.fontSize = 12;
      capLbl.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
      capLbl.paddingRight = "18px";
      row.addControl(capLbl);

      row.onPointerUpObservable.add(() => onSelect(g.slug));
      line.addControl(row);

      // v9 MULTIPLAYER — « Jouer en groupe » : enter/generate a session CODE,
      // then deep-link ?scene=<slug>&group=<CODE>&name=<pseudo> (shareable URL).
      const grp = Button.CreateSimpleButton("grp" + g.slug, "👥");
      grp.width = "60px";
      grp.height = "58px";
      grp.paddingBottom = "8px";
      grp.paddingLeft = "6px";
      grp.thickness = 1;
      grp.color = "#3a6a8a";
      grp.background = "#1d3d54";
      grp.cornerRadius = 12;
      grp.fontSize = 20;
      grp.onPointerUpObservable.add(() => launchGroup(g.slug));
      line.addControl(grp);
    });

    const foot = text(
      t({
        fr: "Astuce : glisse les objets, clique les panneaux, WASD/flèches ou 🎮 manette pour marcher, 👥 jouer en groupe, ⤢ plein écran. Badges : ⌨️ clavier · 🎮 manette supportée · 👆 tactile.",
        en: "Tip: drag objects, click panels, WASD/arrows or 🎮 gamepad to walk, 👥 group play, ⤢ fullscreen. Badges: ⌨️ keyboard · 🎮 gamepad supported · 👆 touch.",
      }),
      13,
      "#7fa6bd",
      "44px",
    );
    foot.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    foot.paddingBottom = "14px";
    panel.addControl(foot);
  }

  hide(): void {
    this.adt?.dispose();
    this.adt = null;
  }
}

/**
 * v9 — a game is "stick-friendly" (🎮 badge) when its DATA shows avatar
 * movement is central: any scene objective of kind reach/guide/collect, OR
 * ≥5 pickup/guide_target interactables across its scenes. Honest, not
 * decorative — purely drag/panel games only get ⌨️👆.
 */
function stickFriendly(g: GameDef): boolean {
  let movement = 0;
  for (const s of g.scenes) {
    for (const o of s.objectives) {
      if (o.kind === "reach" || o.kind === "guide" || o.kind === "collect") return true;
    }
    for (const it of s.interactables) {
      if (it.type === "pickup" || it.type === "guide_target") movement++;
    }
  }
  return movement >= 5;
}

/**
 * v9 MULTIPLAYER — group-play entry: ask for (or generate) a session code and
 * a display name, then reload on the deep-link
 * `?scene=<slug>&group=<CODE>&name=<pseudo>` — the URL itself is what the
 * player shares with the group (same code = same session on the relay).
 */
function launchGroup(slug: string): void {
  const codeIn = window.prompt(
    t({
      fr: "Code de session (laisse vide pour créer un nouveau groupe) :",
      en: "Session code (leave empty to create a new group):",
    }),
    "",
  );
  if (codeIn === null) return; // cancelled
  let code = codeIn.trim().toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 12);
  if (code.replace(/-/g, "").length < 3) code = genSessionCode();
  let prevName = "";
  try {
    prevName = localStorage.getItem("kq_name") || "";
  } catch { /* ignore */ }
  const nameIn = window.prompt(t({ fr: "Ton pseudo :", en: "Your name:" }), prevName);
  if (nameIn === null) return; // cancelled
  const name = nameIn.trim().slice(0, 24) || t({ fr: "Pèlerin", en: "Pilgrim" });
  try {
    localStorage.setItem("kq_name", name);
  } catch { /* ignore */ }
  const url = new URL(location.href);
  url.searchParams.set("scene", slug);
  url.searchParams.set("group", code);
  url.searchParams.set("name", name);
  url.searchParams.delete("idx");
  location.href = url.toString();
}

function loadKeys(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem("kq_keys") || "[]"));
  } catch {
    return new Set<string>();
  }
}

function text(s: string, size: number, color: string, height: string): TextBlock {
  const tb = new TextBlock();
  tb.text = s;
  tb.color = color;
  tb.fontSize = size;
  tb.height = height;
  tb.textWrapping = true;
  return tb;
}
